"use client";

import { useCallback, useEffect, useRef, type RefObject } from "react";

export interface UseTimelineScrollUiEventsInput {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  updateScrollUi: () => void;
}

export function useTimelineScrollUiEvents({
  scrollContainerRef,
  updateScrollUi,
}: UseTimelineScrollUiEventsInput): void {
  const rafRef = useRef<number | null>(null);
  const scheduleScrollUiUpdate = useCallback((): void => {
    if (typeof window === "undefined") {
      return;
    }

    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = window.requestAnimationFrame(() => {
      updateScrollUi();
    });
  }, [updateScrollUi]);

  useEffect(() => {
    const scrollContainerNode = scrollContainerRef.current;
    if (!scrollContainerNode) {
      return undefined;
    }

    scrollContainerNode.addEventListener("scroll", scheduleScrollUiUpdate, {
      passive: true,
    });
    window.addEventListener("resize", scheduleScrollUiUpdate);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => scheduleScrollUiUpdate())
        : null;
    resizeObserver?.observe(scrollContainerNode);

    return () => {
      scrollContainerNode.removeEventListener("scroll", scheduleScrollUiUpdate);
      window.removeEventListener("resize", scheduleScrollUiUpdate);
      resizeObserver?.disconnect();

      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [scheduleScrollUiUpdate, scrollContainerRef]);
}
